import { CheckCircleOutlined, ClockCircleOutlined, ExclamationCircleOutlined, SyncOutlined } from "@ant-design/icons";
import type { CardProps, TableColumnsType, TagProps } from "antd";
import { Alert, Card, Table, Tag } from "antd";
import type { FC } from "react";
import { createElement } from "react";
import { useNavigate } from "react-router";

import type { CampaignState, RecentCampaignDto } from "../types/Dashboard";
import { formatDateTime } from "../utils/formatters";

interface RecentCampaignsCardProps extends CardProps {
    campaigns: RecentCampaignDto[];
    loading?: boolean;
    error?: unknown;
}

/**
 * RecentCampaignsCard - Table-based list of recent campaigns
 */
export const RecentCampaignsCard: FC<RecentCampaignsCardProps> = ({ campaigns, loading = false, error, ...cardProps }) => {
    const navigate = useNavigate();

    const handleRowClick = (record: RecentCampaignDto) => {
        navigate(`campaigns/terminals/view/${record.id}`);
    };

    const getStatusBadge = (
        state: string,
    ): {
        color: TagProps["color"];
        icon: typeof CheckCircleOutlined | typeof ClockCircleOutlined | typeof ExclamationCircleOutlined | typeof SyncOutlined;
    } => {
        const normalizedState = state?.toUpperCase() as CampaignState;

        switch (normalizedState) {
            case "UPDATED":
                return { color: "green-inverse", icon: CheckCircleOutlined };
            case "WAITING":
            case "GENERATING":
                return { color: "blue-inverse", icon: SyncOutlined };
            case "CREATED":
                return { color: "cyan-inverse", icon: ClockCircleOutlined };
            case "STOPPED":
                return { color: "orange-inverse", icon: ClockCircleOutlined };
            case "FAILED":
                return { color: "red-inverse", icon: ExclamationCircleOutlined };
            case "ROLLBACK_WAITING":
            case "ROLLBACK_UPDATED":
                return { color: "purple-inverse", icon: SyncOutlined };
            default:
                return { color: "default", icon: ClockCircleOutlined };
        }
    };

    const columns: TableColumnsType<RecentCampaignDto> = [
        {
            title: "Config Version",
            dataIndex: "confVersion",
            key: "confVersion",
        },
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            ellipsis: true,
        },
        {
            title: "Validity",
            key: "validity",
            render: (_, record) => {
                if (record.validFrom && record.validTo) {
                    return `${formatDateTime(record.validFrom)} - ${formatDateTime(record.validTo)}`;
                }
                if (record.validFrom) {
                    return `From ${formatDateTime(record.validFrom)}`;
                }
                if (record.validTo) {
                    return `Until ${formatDateTime(record.validTo)}`;
                }
                return formatDateTime(record.created);
            },
        },
        {
            title: "Number of Terminals",
            dataIndex: "terminalCount",
            key: "terminalCount",
            align: "center",
        },
        {
            title: "Status",
            dataIndex: "state",
            key: "state",
            render: (state: string) => {
                const { color, icon } = getStatusBadge(state);
                return (
                    <Tag className="text-capitalize" color={color} icon={createElement(icon)}>
                        {state}
                    </Tag>
                );
            },
        },
        {
            title: "Campaign Owner",
            dataIndex: "owner",
            key: "owner",
        },
        {
            title: "Created Date",
            dataIndex: "created",
            key: "created",
            render: (value) => formatDateTime(value),
        },
    ];

    if (error) {
        return (
            <Card title="Recent Campaigns" {...cardProps} style={{ backgroundColor: "#FFFFFF", ...cardProps.style }}>
                <Alert message="Error Loading Campaigns" description={error.toString()} type="error" showIcon />
            </Card>
        );
    }

    return (
        <Card title="Recent Campaigns" {...cardProps} style={{ backgroundColor: "#FFFFFF", ...cardProps.style }}>
            <Table
                columns={columns}
                dataSource={campaigns}
                loading={loading}
                rowKey="id"
                onRow={(record) => ({
                    onClick: () => handleRowClick(record),
                    style: { cursor: "pointer" },
                })}
                pagination={{
                    pageSize: 10,
                    showSizeChanger: false,
                }}
                locale={{
                    emptyText: "No recent campaigns found. Create your first campaign to get started!",
                }}
                scroll={{ x: true }}
            />
        </Card>
    );
};
